import React from 'react';
import styled from '@emotion/styled';
import { Button, Modal, TextArea, Checkbox, Icon } from 'semantic-ui-react';
import { boolSiNo, textArea } from '../../../components/helpers';
import moment from 'moment';
import membersForms from '../../../services/api/membersForms';
import Swal from 'sweetalert2/dist/sweetalert2.js';

const ModalBody = styled.div`
  text-align: left;
  color: #1a1a1a;

  .modal-title {
    margin: 0 0 1.25rem;
    font-size: 1.45rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .section {
    margin-bottom: 1.25rem;
  }

  .section-card {
    background: #f8fafc;
    border: 1px solid #e8edf2;
    border-radius: 14px;
    padding: 1.1rem 1.2rem;
  }

  .section-card--soft {
    background: #f3f9fc;
    border-color: #d8ecf5;
  }

  .section-label {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7a8a96;
    margin: 0 0 0.75rem;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem 1.25rem;

    @media (max-width: 560px) {
      grid-template-columns: 1fr;
    }
  }

  .info-item {
    margin: 0;

    .label {
      display: block;
      font-size: 0.72rem;
      font-weight: 600;
      color: #7a8a96;
      margin-bottom: 0.15rem;
    }

    .value {
      font-size: 0.95rem;
      font-weight: 600;
      color: #1a1a1a;
      word-break: break-word;
    }
  }

  .info-item--full {
    grid-column: 1 / -1;
  }

  .inline-action {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 1rem;
    padding-top: 0.9rem;
    border-top: 1px solid #e2e8f0;
  }

  .inline-action p {
    margin: 0;
    font-size: 0.9rem;
    color: #555;
  }

  .group-status {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.65rem 1rem;
    margin-bottom: 0.85rem;
  }

  .group-status-text {
    margin: 0;
    font-size: 0.95rem;

    strong {
      font-weight: 700;
    }
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 700;
    background: #e8edf2;
    color: #444;
  }

  .status-pill--yes {
    background: #e8f6fc;
    color: #1a6f8f;
    border: 1px solid #c5e8f5;
  }

  .status-pill--no {
    background: #f1f3f5;
    color: #555;
    border: 1px solid #dde2e6;
  }

  .status-pill--pending {
    background: #fff8e6;
    color: #8a6d1b;
    border: 1px solid #f0e0a8;
  }

  .membership-list {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    margin: 0 0 0.85rem;
  }

  .membership-item {
    padding: 0.7rem 0.85rem;
    background: #fff;
    border: 1px solid #d8ecf5;
    border-radius: 10px;
  }

  .membership-item p {
    margin: 0.2rem 0 0;
    font-size: 0.88rem;
    color: #444;
  }

  .membership-item .group-name {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: #1a1a1a;
  }

  .cta-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.65rem;
  }

  .cta-hint {
    margin: 0;
    font-size: 0.85rem;
    color: #666;
    flex: 1;
    min-width: 180px;
  }

  .notes-label {
    margin: 0 0 0.55rem;
    font-size: 0.95rem;
    font-weight: 700;
  }

  .contacted-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    background: #fff;
    border: 1px solid #e8edf2;
    border-radius: 12px;
  }

  .actions-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    margin-top: 1.25rem;
    padding-top: 0.25rem;

    .ui.button {
      margin: 0 !important;
    }

    .ui.button.primary {
      background: #1a1a1a !important;
      color: #fff !important;
    }
  }
`;

const profileQuery = (identification, phone) =>
  `identification=${encodeURIComponent(identification || '')}&phone=${encodeURIComponent(
    phone || ''
  )}&edit=1`;

const getGuestGroupMemberships = (guest) => {
  const memberships = [...(guest?.male || []), ...(guest?.female || [])];
  return Object.values(
    memberships.reduce((acc, membership) => {
      if (membership?.id) acc[membership.id] = membership;
      return acc;
    }, {})
  );
};

const membershipStatusLabel = (membership) => {
  if (membership?.pending) return 'Pendiente de aprobación';
  return 'Activo';
};

const NewMemberModal = (props) => {
  const { set_showNewMembers, showNewMembers, newMember, changeNewMembersLine } = props;
  const {
    contacted,
    tutorObservations,
    Guest,
    country,
    comments,
    toPray,
    createdAt,
    updatedAt,
    interestedCrecer,
    interestedGroup,
    preferredContact,
    interestedInChurch,
    kindGroup,
  } = newMember;
  const { name, lastName, phone, email, identification, birthdate, gender } = Guest;
  const groupMemberships = getGuestGroupMemberships(Guest);
  const belongsToGroup = groupMemberships.length > 0;
  const [inputs, setInputs] = React.useState({});
  const [loading, set_loading] = React.useState(false);

  React.useEffect(() => {
    setInputs({
      interestedCrecer,
      interestedGroup: kindGroup ? 'Sí' : interestedGroup,
      interestedInChurch,
      contacted,
      tutorObservations,
      toPray,
      comments,
      kindGroup,
    });
  }, [newMember]);

  function updateNewMember() {
    let body = inputs;
    body.guest_id = Guest.id;

    set_loading(true);
    membersForms
      .updateNewMembers(body)
      .then((response) => {
        set_loading(false);
        if (response.data.success) {
          Swal.fire('Actualizado satisfatoriamente');
          set_showNewMembers(null);
          changeNewMembersLine(response.data.newMemberUpdated);
        }
      })
      .catch((error) => {
        set_loading(false);
        if (error.response) Swal.fire(error.response.data.message);
      });
  }

  const handleInputs = (event) => {
    let name = event.target.name;
    let value = event.target.value;
    setInputs({ ...inputs, [name]: value });
  };

  const handleCheckers = (name) => setInputs({ ...inputs, [name]: !inputs[name] });

  const groupInterest = inputs.interestedGroup || interestedGroup;
  const qs = profileQuery(identification, phone);

  return (
    <Modal open={showNewMembers === newMember.id} onClose={() => set_showNewMembers(null)}>
      <Modal.Content>
        <Modal.Description>
          <ModalBody>
            <h2 className="modal-title">Formulario de {name}</h2>

            <section className="section section-card">
              <span className="section-label">Datos de la persona</span>
              <div className="info-grid">
                <p className="info-item">
                  <span className="label">Cédula</span>
                  <span className="value">{identification || '—'}</span>
                </p>
                <p className="info-item">
                  <span className="label">Nombre</span>
                  <span className="value">
                    {name} {lastName}
                  </span>
                </p>
                {!!birthdate && (
                  <p className="info-item">
                    <span className="label">Edad</span>
                    <span className="value">{moment().diff(birthdate, 'years')} años</span>
                  </p>
                )}
                <p className="info-item">
                  <span className="label">Celular</span>
                  <span className="value">{phone || '—'}</span>
                </p>
                {!!email && (
                  <p className="info-item">
                    <span className="label">E-mail</span>
                    <span className="value">{email}</span>
                  </p>
                )}
                {!!country && (
                  <p className="info-item">
                    <span className="label">País</span>
                    <span className="value">{country}</span>
                  </p>
                )}
                {!!gender && (
                  <p className="info-item">
                    <span className="label">Género</span>
                    <span className="value">{gender}</span>
                  </p>
                )}
                <p className="info-item">
                  <span className="label">Preferencia de contacto</span>
                  <span className="value">{preferredContact || 'No especificado'}</span>
                </p>
                <p className="info-item">
                  <span className="label">Fecha de llenado</span>
                  <span className="value">
                    {moment.tz(createdAt, 'America/Bogota').format('DD-MM-YYYY hh:mm a')}
                  </span>
                </p>
                <p className="info-item info-item--full">
                  <span className="label">Última interacción</span>
                  <span className="value">
                    {moment.tz(updatedAt, 'America/Bogota').format('DD-MM-YYYY hh:mm a')}
                  </span>
                </p>
              </div>

              <div className="inline-action">
                <p>¿Hay algún dato erróneo?</p>
                <Button
                  as="a"
                  href={`/perfil/info?${qs}`}
                  target="_blank"
                  rel="noreferrer"
                  size="tiny"
                >
                  <Icon name="external alternate" /> Ir a corregir
                </Button>
              </div>
            </section>

            <section className="section section-card section-card--soft">
              <span className="section-label">Interés en grupo</span>
              <div className="group-status">
                <p className="group-status-text">
                  <strong>¿Está interesado en asistir a un grupo?</strong>
                </p>
                <span
                  className={`status-pill ${
                    groupInterest === 'Sí'
                      ? 'status-pill--yes'
                      : groupInterest === 'No'
                      ? 'status-pill--no'
                      : ''
                  }`}
                >
                  {groupInterest || 'No especificado'}
                </span>
              </div>

              <div className="group-status">
                <p className="group-status-text">
                  <strong>¿Ya está en un grupo?</strong>
                </p>
                <span
                  className={`status-pill ${
                    belongsToGroup ? 'status-pill--yes' : 'status-pill--no'
                  }`}
                >
                  {belongsToGroup ? 'Sí' : 'No'}
                </span>
              </div>

              {belongsToGroup && (
                <div className="membership-list">
                  {groupMemberships.map((membership) => (
                    <div className="membership-item" key={membership.id}>
                      <p className="group-name">
                        {membership.ChurchGroup?.groupName || 'Grupo sin nombre'}
                      </p>
                      <p>
                        Estado como miembro:{' '}
                        <span
                          className={`status-pill ${
                            membership.pending ? 'status-pill--pending' : 'status-pill--yes'
                          }`}
                        >
                          {membershipStatusLabel(membership)}
                        </span>
                      </p>
                      {!!membership.statusObservation && (
                        <p>Observación: {membership.statusObservation}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="cta-row">
                <p className="cta-hint">Revisa el perfil de la persona en este enlace.</p>
                <Button
                  as="a"
                  primary
                  href={`/perfil/grupos?${qs}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="users" /> Ver perfil / grupo
                </Button>
              </div>

              {!belongsToGroup && (
                <div className="cta-row">
                  <p className="cta-hint">Puedes inscribir a la persona en un grupo ahora.</p>
                  <Button
                    as="a"
                    primary
                    href={`/grupos/registrarse?${qs}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="plus" /> Ir a seleccionar grupo
                  </Button>
                </div>
              )}
            </section>

            <section className="section section-card">
              <span className="section-label">Notas del contacto</span>
              <p className="notes-label">Observaciones al contacto</p>
              {textArea({
                TextArea,
                handler: handleInputs,
                type: 'text',
                label: 'Escribe aquí las notas de la llamada…',
                name: 'tutorObservations',
                value: inputs.tutorObservations,
              })}
            </section>

            <section className="section section-card">
              <span className="section-label">Finalización de llamada</span>
              <div className="contacted-row">
                <Checkbox
                  label={`Marcar como contactado — ${boolSiNo(inputs.contacted)}`}
                  name="contacted"
                  checked={!!inputs.contacted}
                  toggle
                  onClick={() => handleCheckers('contacted')}
                />
              </div>

              <div className="actions-row">
                <Button
                  primary
                  loading={loading}
                  disabled={loading}
                  onClick={() => updateNewMember()}
                >
                  Guardar cambios
                </Button>
                <Button onClick={() => set_showNewMembers(null)}>Cerrar</Button>
              </div>
            </section>
          </ModalBody>
        </Modal.Description>
      </Modal.Content>
    </Modal>
  );
};

export default NewMemberModal;
